import { authFetch } from './api.js';
import { registerViewLoader } from './navigation.js';
import { formatDate, renderPagination, showToast } from './utils.js';
import { readScopedQuery, writeScopedQuery } from './url-state.js';
import { withTableLoading } from './view-state.js';

const QUERY_SCHEMA = {
  page: { type: 'int', defaultValue: 1 },
  actionType: { type: 'string', defaultValue: '' },
  status: { type: 'string', defaultValue: '' },
  days: { type: 'string', defaultValue: '7' },
  keyword: { type: 'string', defaultValue: '' },
};

const ACTION_LABELS = {
  credentials_update: '更新凭证',
  auth_login_refresh: '刷新登录态',
  model_sync: '同步模型',
  collect_submit: '提交采集',
  collect_completed: '采集完成',
  collect_failed: '采集失败',
  collect_cancel_request: '请求取消',
  collect_cancelled: '已取消',
  analyze_product: '单商品分析',
};

const STATUS_LABELS = {
  success: '成功',
  queued: '排队',
  failed: '失败',
  cancelled: '已取消',
};

const STATUS_COLORS = {
  success: '#10b981',
  queued: '#f59e0b',
  failed: '#ef4444',
  cancelled: '#a855f7',
};

function formatLogMessage(row) {
  if (row?.message) return String(row.message).trim();
  return '-';
}

function formatShortJobId(jobId) {
  const value = String(jobId || '').trim();
  if (!value) return '-';
  if (value.length <= 18) return value;
  return `${value.slice(0, 8)}...${value.slice(-4)}`;
}

export function initProductResearchLogs() {
  hydrateFilters();
  registerViewLoader('admin-product-research-logs', () => loadProductResearchLogs());
  document.getElementById('product-research-log-search-btn')?.addEventListener('click', () => loadProductResearchLogs(1));
  document.getElementById('product-research-log-action-filter')?.addEventListener('change', () => loadProductResearchLogs(1));
  document.getElementById('product-research-log-status-filter')?.addEventListener('change', () => loadProductResearchLogs(1));
  document.getElementById('product-research-log-days-filter')?.addEventListener('change', () => loadProductResearchLogs(1));
  document.getElementById('product-research-log-keyword')?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') loadProductResearchLogs(1);
  });
}

function hydrateFilters() {
  const state = readScopedQuery('productresearchlogs', QUERY_SCHEMA);
  const mapping = {
    'product-research-log-action-filter': state.actionType || '',
    'product-research-log-status-filter': state.status || '',
    'product-research-log-days-filter': state.days || '7',
    'product-research-log-keyword': state.keyword || '',
  };
  Object.entries(mapping).forEach(([id, value]) => {
    const el = document.getElementById(id);
    if (el) el.value = value;
  });
}

export async function loadProductResearchLogs(page = null) {
  const state = readScopedQuery('productresearchlogs', QUERY_SCHEMA);
  const targetPage = page == null ? state.page : page;
  const actionType = document.getElementById('product-research-log-action-filter')?.value || '';
  const status = document.getElementById('product-research-log-status-filter')?.value || '';
  const days = document.getElementById('product-research-log-days-filter')?.value || '7';
  const keyword = document.getElementById('product-research-log-keyword')?.value?.trim() || '';
  writeScopedQuery('productresearchlogs', { page: targetPage, actionType, status, days, keyword });

  const params = new URLSearchParams({ page: String(targetPage), size: '20' });
  if (actionType) params.set('actionType', actionType);
  if (status) params.set('status', status);
  if (days) params.set('days', days);
  if (keyword) params.set('keyword', keyword);

  const tbody = document.getElementById('product-research-log-tbody');
  const data = await withTableLoading({
    tbody,
    colSpan: 9,
    page: targetPage,
    errorMessage: '加载选品操作日志失败',
    onRetry: (p) => loadProductResearchLogs(p),
    onError: () => showToast('加载选品操作日志失败', 'error'),
    task: async () => {
      const resp = await authFetch(`/api/admin/product-research/logs?${params.toString()}`);
      if (!resp.ok) throw new Error();
      return resp.json();
    },
  });
  if (!data) return;

  renderProductResearchLogHeader();

  if (!Array.isArray(data.rows) || data.rows.length === 0) {
    tbody.replaceChildren(createProductResearchLogEmptyRow());
    document.getElementById('product-research-log-pagination').replaceChildren();
    renderPageMeta(0, 1, 20);
    window.__productResearchLogRowsCache = {};
    return;
  }

  window.__productResearchLogRowsCache = Object.fromEntries(data.rows.map((row) => [String(row.id), row]));
  tbody.replaceChildren(...data.rows.map(createProductResearchLogRow));

  renderPageMeta(data.total, data.page, data.size);
  renderPagination('product-research-log-pagination', data.total, data.page, data.size, (p) => loadProductResearchLogs(p));
}

function createProductResearchLogElement(tagName, className = '', text = '') {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== '') element.textContent = text;
  return element;
}

function createProductResearchLogCell(label, className = '') {
  const cell = createProductResearchLogElement('td', className);
  cell.dataset.label = label;
  return cell;
}

function renderProductResearchLogHeader() {
  const header = document.getElementById('product-research-log-thead');
  if (!header) return;
  const row = document.createElement('tr');
  ['日志 ID', '用户', '任务类型', '站点 / 模型', '状态', '任务', '说明', '创建时间', '操作']
    .forEach((label) => row.appendChild(createProductResearchLogElement('th', '', label)));
  header.replaceChildren(row);
}

function createProductResearchLogEmptyRow() {
  const row = document.createElement('tr');
  const cell = createProductResearchLogElement('td', 'pr-logs-empty-cell', '暂无日志');
  cell.colSpan = 9;
  row.appendChild(cell);
  return row;
}

function createProductResearchLogRow(row) {
  const record = document.createElement('tr');
  record.className = 'pr-logs-row';
  const actionLabel = ACTION_LABELS[row.action_type] || row.action_type || '';
  const statusLabel = STATUS_LABELS[row.status] || row.status || '';
  const objectText = [row.site_code, row.model_code].filter(Boolean).join(' / ') || '-';
  const message = formatLogMessage(row);

  const idCell = createProductResearchLogCell('日志 ID');
  const idWrap = createProductResearchLogElement('div', 'pr-logs-id-cell');
  idWrap.appendChild(createProductResearchLogElement('div', 'pr-logs-id-main mono', `LOG-${String(row.id ?? '').padStart(6, '0')}`));
  idCell.appendChild(idWrap);

  const userCell = createProductResearchLogCell('用户');
  userCell.appendChild(createProductResearchLogElement('div', 'pr-logs-user-main', row.user_name || '系统'));
  if (row.user_email) userCell.appendChild(createProductResearchLogElement('div', 'pr-logs-user-sub', row.user_email));

  const actionCell = createProductResearchLogCell('任务类型');
  actionCell.appendChild(createProductResearchLogElement('div', 'pr-logs-action-main', actionLabel));
  if (row.error_code) actionCell.appendChild(createProductResearchLogElement('div', 'pr-logs-action-sub', `错误码 ${row.error_code}`));

  const objectCell = createProductResearchLogCell('站点 / 模型');
  objectCell.appendChild(createProductResearchLogElement('div', 'pr-logs-object-main', objectText));
  const statusCell = createProductResearchLogCell('状态');
  statusCell.appendChild(createProductResearchLogElement('span', `badge ${getStatusBadgeClass(row.status)}`, statusLabel));

  const taskCell = createProductResearchLogCell('任务');
  if (row.job_id) {
    const taskId = createProductResearchLogElement('div', 'pr-logs-task-id mono', formatShortJobId(row.job_id));
    taskId.title = String(row.job_id);
    taskCell.appendChild(taskId);
    if (row.target_count) taskCell.appendChild(createProductResearchLogElement('div', 'pr-logs-task-sub', `目标 ${row.target_count} 条`));
  } else taskCell.textContent = '-';

  const messageCell = createProductResearchLogCell('说明');
  const messageNode = createProductResearchLogElement('div', 'pr-logs-message', message);
  messageNode.title = message;
  messageCell.appendChild(messageNode);
  const timeCell = createProductResearchLogCell('创建时间', 'pr-logs-time');
  timeCell.textContent = formatDate(row.created_at);
  const actionButtonCell = createProductResearchLogCell('操作');
  const detailButton = createProductResearchLogElement('button', 'btn btn-secondary btn-sm', '详情');
  detailButton.type = 'button';
  detailButton.dataset.action = 'show-product-research-log-detail';
  detailButton.dataset.id = String(row.id ?? '');
  actionButtonCell.appendChild(detailButton);
  record.append(idCell, userCell, actionCell, objectCell, statusCell, taskCell, messageCell, timeCell, actionButtonCell);
  return record;
}

function getStatusBadgeClass(status) {
  return {
    success: 'badge-active',
    queued: 'badge-running',
    failed: 'badge-disabled',
    cancelled: 'badge-member',
  }[status] || 'badge-member';
}

function renderPageMeta(total, page, size) {
  const el = document.getElementById('product-research-log-page-meta');
  if (!el) return;
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / Math.max(1, Number(size) || 20)));
  el.textContent = `第 ${page} 页 / 共 ${totalPages} 页`;
}

export function showProductResearchLogDetail(id) {
  const row = window.__productResearchLogRowsCache?.[String(id)];
  if (!row) {
    showToast('请刷新后重试', 'error');
    return;
  }

  const detail = [
    `用户: ${row.user_name || '系统'}${row.user_email ? ` <${row.user_email}>` : ''}`,
    `操作: ${ACTION_LABELS[row.action_type] || row.action_type}`,
    `结果: ${STATUS_LABELS[row.status] || row.status}`,
    row.site_code ? `站点: ${row.site_code}` : '',
    row.model_code ? `模型: ${row.model_code}` : '',
    row.job_id ? `任务ID: ${row.job_id}` : '',
    row.target_count ? `目标条数: ${row.target_count}` : '',
    formatLogMessage(row) !== '-' ? `说明: ${formatLogMessage(row)}` : '',
    `时间: ${formatDate(row.created_at)}`,
    row.metadata_json ? `\n━━━ 详情 ━━━\n${JSON.stringify(row.metadata_json, null, 2)}` : '',
  ].filter(Boolean).join('\n');

  document.getElementById('detail-modal-title').textContent = `选品日志 #${row.id}`;
  document.getElementById('detail-modal-content').textContent = detail;
  document.getElementById('detail-modal').hidden = false;
}
